import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../billing/shared';

const num = (label, k, form, set, step, testid) => (
  <label className="block text-xs text-slate-600">{label}
    <input type="number" step={step} value={form[k]} onChange={(e) => set(k, e.target.value)} className={`${inputCls} mt-1 w-28`} data-testid={testid} />
  </label>
);

export const RatingBonusSettings = ({ onSaved }) => {
  const { authHeader } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/payroll/settings`, { headers: authHeader() }).then(({ data }) => setForm({
      rating_bonus_enabled: !!data.rating_bonus_enabled, rating_bonus_amount: data.rating_bonus_amount ?? 500,
      rating_bonus_min_avg: data.rating_bonus_min_avg ?? 4.5, rating_bonus_min_count: data.rating_bonus_min_count ?? 5,
    })).catch(() => {});
  }, [authHeader]);
  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/payroll/settings`, {
        ...form, rating_bonus_amount: Number(form.rating_bonus_amount), rating_bonus_min_avg: Number(form.rating_bonus_min_avg),
        rating_bonus_min_count: Number(form.rating_bonus_min_count),
      }, { headers: authHeader() });
      toast.success('Rating bonus saved');
      onSaved?.();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };
  return (
    <section className="mb-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="rating-bonus-settings">
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-[220px] flex-1">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Star size={15} className="text-amber-400" fill="currentColor" /> Customer rating bonus</h3>
          <p className="mt-0.5 text-xs text-slate-500">A fixed monthly bonus for technicians whose customer ratings that month (on jobs they worked on) clear the bar.</p>
          <label className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-slate-700">
            <input type="checkbox" checked={form.rating_bonus_enabled} onChange={(e) => set('rating_bonus_enabled', e.target.checked)} data-testid="rating-bonus-enabled" /> Turn on
          </label>
        </div>
        {num('Bonus (₹)', 'rating_bonus_amount', form, set, '50', 'rating-bonus-amount')}
        {num('Minimum average ★', 'rating_bonus_min_avg', form, set, '0.1', 'rating-bonus-min-avg')}
        {num('Minimum ratings', 'rating_bonus_min_count', form, set, '1', 'rating-bonus-min-count')}
        <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg bg-teal-500 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-600 disabled:bg-teal-300" data-testid="rating-bonus-save">
          {saving && <Loader2 size={14} className="animate-spin" />} Save
        </button>
      </div>
    </section>
  );
};

export const RatingBonusNote = ({ rb, staffId }) => {
  if (!rb || !rb.enabled) return null;
  if (rb.amount > 0) return <div className="text-[10px] text-amber-600" data-testid={`row-rating-bonus-${staffId}`}>+ ₹{rb.amount} rating bonus ({rb.average}★ · {rb.count})</div>;
  if (!rb.count) return null;
  return <div className="text-[10px] text-slate-400" data-testid={`row-rating-bonus-${staffId}`}>{rb.average}★ from {rb.count} (needs {rb.min_avg}★ · {rb.min_count})</div>;
};
